import type { Ping } from '../../shared/wire';
import { MOCK } from './device';
import { state } from './app-state';

// Group consecutive pings into "sessions" by server-supplied session_id
// when present, falling back to time-gap clustering for runs of pings
// with no session_id. This is the local-device view: each contiguous
// run sharing the same non-null session_id is one session; runs of null
// session_id break on the same GAP_MIN gap rule as the legacy clustering.
export function groupSessionsLocal(pings: Ping[]){
  if(!pings.length) return [];
  const groups = [];
  let cur = { sessionId: pings[0].session_id ?? null, points: [pings[0]], startT: pings[0].t, endT: pings[0].t };
  for(let i=1;i<pings.length;i++){
    const p = pings[i];
    const pid = p.session_id ?? null;
    if(pid !== null && pid === cur.sessionId){
      cur.points.push(p);
      cur.endT = p.t;
    } else if(pid === null && cur.sessionId === null){
      // Null-run: fall back to gap clustering so legacy / no-session pings
      // still segment sensibly on long pauses.
      if(p.t - cur.endT > GAP_MIN*60*1000){
        groups.push(cur);
        cur = { sessionId: null, points: [p], startT: p.t, endT: p.t };
      } else {
        cur.points.push(p);
        cur.endT = p.t;
      }
    } else {
      groups.push(cur);
      cur = { sessionId: pid, points: [p], startT: p.t, endT: p.t };
    }
  }
  groups.push(cur);
  return groups.reverse().map((s, i) => ({
    id: 's'+i,
    sessionId: s.sessionId,
    startT: s.startT,
    endT: s.endT,
    points: s.points,
    coords: s.points.map(p => [p.lat, p.lng]),
    cadence_s: Math.max(1, Math.round((s.endT - s.startT) / 1000 / Math.max(1, s.points.length - 1))),
  }));
}

// Gap threshold (minutes) for the null-session fallback in
// groupSessionsLocal: a run of pings with no server session_id is split
// into separate sessions wherever the inter-ping gap exceeds this.
export const GAP_MIN = 15;

// Distinguishable polyline palette. Index 0 is the reserved local/selected
// accent (#B84A22): the local device, and the selected session, are always
// forced to it at draw time. colorForToken() therefore hashes into indices
// 1..N-1 ONLY — never index 0 — so no peer device or unselected session can
// be drawn in the accent and masquerade as "you" / the active pick.
export const COHORT_PALETTE = [
  '#B84A22', '#2C7A7B', '#6B46C1', '#C05621', '#2F855A', '#1F4E8C',
  '#B83280', '#975A16', '#3182CE', '#9F7AEA', '#38A169', '#D69E2E',
];
export function colorForToken(token: string){
  let h = 0;
  for(let i=0;i<token.length;i++) h = (h * 31 + token.charCodeAt(i)) & 0xfffffff;
  // Skip index 0 (the reserved accent); hash across the remaining N-1 slots.
  return COHORT_PALETTE[1 + (h % (COHORT_PALETTE.length - 1))];
}

// Mirrors the server's groupDeviceCapFor() / DEVICE_CAP (functions/_shared/
// cohort_query.ts). The server now hard-caps group membership at this
// number, so the live cohort is bounded at steady state — but the poll
// never signals device removal, so reaper-delete + re-join churn over a
// long session can still grow state.cohort past the cap. This is a
// defence-in-depth trim, not the primary bound.
export const COHORT_CLIENT_CAP = 64;

export function dedupePings(pings: Ping[]){
  // Stable dedup keyed on (t, lat, lng), time-ascending. Server returns
  // ascending too. Keying on position as well as time keeps two genuinely
  // distinct fixes that happen to share a millisecond from collapsing into
  // one (a bare t-key dropped the second silently), and lets the polling
  // fallback re-fetch an overlapping window without surfacing duplicates.
  const seen = new Set();
  const out = [];
  for(const p of pings){
    const k = `${p.t}:${p.lat}:${p.lng}`;
    if(seen.has(k)) continue;
    seen.add(k);
    out.push(p);
  }
  return out;
}

export function trimCohort(){
  if(state.cohort.size <= COHORT_CLIENT_CAP) return;
  // Keep the local device plus the newest-by-lastT peers up to the cap.
  const localToken = MOCK.device?.token;
  const entries = Array.from(state.cohort.values())
    .sort((a,b) => (b.lastT || 0) - (a.lastT || 0));
  const keep = new Set<string>();
  if(localToken && state.cohort.has(localToken)) keep.add(localToken);
  for(const e of entries){
    if(keep.size >= COHORT_CLIENT_CAP) break;
    keep.add(e.token);
  }
  for(const token of state.cohort.keys()){
    if(!keep.has(token)) state.cohort.delete(token);
  }
}

// The cohort `/recent` snapshot echoes the local device back like any other
// member, but under an opaque cohort id (HMAC of the token, ADR 0015) — NOT the
// raw token — so it can't be matched against MOCK.device.token. Left as-is it
// merges in as a second "peer" row beside the row seedLocalDeviceInCohort()
// keys by the raw token, duplicating the current device in "By device" (and
// inflating the peer count + suppressing the "invite a friend" hint). Drop the
// echo by the one identity the snapshot carries verbatim — the device's label
// (animals) and created_at — so the seeded local row stays the single source of
// truth (and keeps full-precision pings, not the cohort-coarsened self-echo).
export function isSelfCohortDevice(remote: any): boolean {
  if(!MOCK.device) return false;
  const selfCreated = Date.parse(MOCK.device.created);
  if(!Number.isFinite(selfCreated) || remote.created_at !== selfCreated) return false;
  const a = MOCK.device.animals, b = remote.animals;
  if(!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  return a.every((k: string, i: number) => k === b[i]);
}

// Drop any self-echo already in the cohort. Belt-and-suspenders for the boot
// race: the first poll can merge the echo before the self-read reconciles
// MOCK.device.created, so identifying self only becomes possible afterwards.
// The raw-token seeded row (key === MOCK.device.token) is never a candidate.
export function purgeSelfCohortEcho(){
  if(!MOCK.device) return;
  for(const [key, dev] of state.cohort){
    if(key !== MOCK.device.token && isSelfCohortDevice(dev)) state.cohort.delete(key);
  }
}

export function mergeCohortDevice(remote: any){
  const existing = state.cohort.get(remote.token);
  const merged = existing
    ? dedupePings([...existing.pings, ...remote.pings].sort((a,b) => a.t - b.t))
    : remote.pings;
  const lastT = merged.length ? merged[merged.length - 1].t : (existing?.lastT ?? remote.created_at);
  state.cohort.set(remote.token, {
    token: remote.token,
    animals: remote.animals,
    created_at: remote.created_at,
    pings: merged,
    truncated: !!remote.truncated || !!existing?.truncated,
    lastT,
    cohort_scope: remote.cohort_scope ?? existing?.cohort_scope,
    tag: remote.tag ?? existing?.tag ?? null,
  });
}

export function seedLocalDeviceInCohort(){
  // Ensure the local device's row appears in the sidebar even when the
  // server hasn't echoed it back yet (brand-new device, propagation lag,
  // or 404 on empty cohort).
  if(!MOCK.device) return;
  if(state.cohort.has(MOCK.device.token)) return;
  state.cohort.set(MOCK.device.token, {
    token: MOCK.device.token,
    animals: MOCK.device.animals,
    created_at: Date.parse(MOCK.device.created) || Date.now(),
    pings: MOCK.pings.slice(),
    truncated: false,
    lastT: MOCK.pings.length ? MOCK.pings[MOCK.pings.length - 1].t : Date.now(),
  });
}
